import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AutoComplete, Button, Modal, Typography } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AwesomeDebouncePromise from 'awesome-debounce-promise';

import type { PaginatedProfileList, Profile } from 'src/api-sdk';
import { ProfilesApi } from 'src/api-sdk';
import { useAuth } from 'src/util/use-auth';
import { useAPI } from 'src/util/use-api';
import { Loader } from 'src/components/Loader';
import { QUERIES } from 'src/util/globals';

interface SwitchProfileModalProps {
  visible: boolean;
  onClose: () => void;
}

export const SwitchProfileModal: React.FC<SwitchProfileModalProps> = ({
  visible,
  onClose,
}) => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const { t } = useTranslation('menu');
  const { user } = useAuth();
  const { api, configuration } = useAPI();
  const [searchQuery, setSearchQuery] = React.useState<string | null>(null);
  const queryClient = useQueryClient();

  const getProfiles = useCallback(() => {
    return new Promise<PaginatedProfileList>((resolve, reject) => {
      new ProfilesApi(configuration, undefined, api)
        .profilesList({ query: searchQuery || undefined })
        .then((response) => {
          resolve(response?.data);
        })
        .catch((error) => reject(error));
    });
  }, [api, configuration, searchQuery]);

  const {
    data: profiles,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [QUERIES.PROFILES],
    queryFn: getProfiles,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedRefetch = useCallback(
    AwesomeDebouncePromise(() => {
      return refetch().catch((error) => {
        console.error('Failed to fetch profiles:', error);
      });
    }, 200),
    [refetch]
  );

  const handleSwitchProfile = () => {
    new ProfilesApi(configuration, undefined, api)
      .profilesUserDetailsPartialUpdate({
        patchedUserUpdateRequest: { active_profile: profile?.external_id },
      })
      .then(() => {
        queryClient.invalidateQueries().then(onClose);
      });
  };

  return (
    <Modal
      title={t('Switch Profile')}
      open={visible}
      onCancel={onClose}
      destroyOnClose
      footer={
        <>
          <Button onClick={onClose}>{t('Cancel')}</Button>
          <Button type="primary" onClick={handleSwitchProfile}>
            {t('Switch Profile')}
          </Button>
        </>
      }
    >
      <>
        <Typography.Title
          level={5}
          style={{ textAlign: 'center', marginBottom: 15 }}
        >
          {t('Current Profile')}: {user?.active_profile?.name}
        </Typography.Title>
        <Loader loading={isFetching}>
          <AutoComplete
            value={searchQuery}
            options={(profiles?.results || []).map((profile: Profile) => ({
              value: profile.name,
              profile: profile,
              label: profile.name,
            }))}
            style={{ width: '100%' }}
            // @ts-ignore
            onSelect={(label, option) => {
              setProfile(option.profile);
            }}
            // @ts-ignore
            onSearch={debouncedRefetch}
            onChange={setSearchQuery}
            placeholder={t('Profile Name')}
          />
        </Loader>
      </>
    </Modal>
  );
};
